import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '../common/FormLayout.vue'
import CreatedInfo from '../common/CreatedInfo.vue'
import VsWrapper from '../vision/VsWrapper.vue'
import { useStore } from 'vuex-composition-helpers'
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import {
  ACTIVE,
  ASSIGNED_TO_SERVICE_PROVIDER,
  CANCELLED,
  COMPLETED,
  PAUSED,
  REQUESTED,
  SCHEDULED,
} from '@/models/job'
import {
  onBeforeRouteUpdate,
  useRoute,
  useRouter,
} from 'vue-router/composables'
import { usePagination } from '@/components/jobs/pagination'
import { useJobsQuery } from '@/components/jobs/query'
import { useJobsSearch } from '@/components/jobs/search'
import { useBatchViewFilters } from '@/components/jobs/filters'
import { useBulkUpdate } from '@/components/jobs/bulk-update'
import BulkUpdateForm from '@/components/jobs/BulkUpdateForm.vue'
import RightDrawer from '@/components/common/RightDrawer.vue'
import {
  useGetSyncOptionsQuery,
  useInvoiceQuery,
  useInvoiceSyncTargetsQuery,
} from '@/api/invoice'
import moment from 'moment'
import JobCard from '@/components/jobs/job-search-page/JobCard.vue'
import BatchSideSheetFilter from '@/components/batches/BatchSideSheetFilter.vue'
import BatchSideSheetProgressBar from '@/components/batches/BatchSideSheetProgressBar.vue'
import { BatchPaymentTerms, batchPaymentTermsString } from '@/models/invoice'
import BatchEditForm from '@/components/batches/BatchEditForm.vue'
import BatchSyncCard from '@/components/batches/BatchSyncCard.vue'
import {
  useBatchCsvExport,
  useBatchPdfExport,
} from '@/components/batches/export'
import { useCurrencyFormatter } from '@/utils/number-formatting'
import VsButton from '@/components/vision/VsButton.vue'
import VsText from '@/components/vision/VsText.vue'
import JobSearchPagination from '@/components/jobs/job-search-page/JobSearchPagination.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import { JobSearchResponse } from '@/models/types/job-search-response'

interface Props {
  id: string
  parentRouteName: string
  parentRouteParams?: Record<string, string>
}


export default /*#__PURE__*/_defineComponent({
  __name: 'Batch',
  props: {
    id: null,
    parentRouteName: null,
    parentRouteParams: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props as { id: string, parentRouteName: string, parentRouteParams: Record<string, string> }

const orderedStatuses = ['completed', 'active', 'unstarted']
const jobStatuses = [
  {
    id: REQUESTED,
    name: 'Requested',
  },
  {
    id: ASSIGNED_TO_SERVICE_PROVIDER,
    name: 'Assigned To Service Provider',
  },
  {
    id: SCHEDULED,
    name: 'Scheduled',
  },
  {
    id: ACTIVE,
    name: 'Active',
  },
  {
    id: PAUSED,
    name: 'Paused',
  },
  {
    id: COMPLETED,
    name: 'Completed',
  },
  {
    id: CANCELLED,
    name: 'Cancelled',
  },
]

const formatter = useCurrencyFormatter()



const store = useStore()
const route = useRoute()
const router = useRouter()

// Local Data/State
const snackbarMessage = ref<string | null>(null)
const showSnackbarError = ref<boolean>(false)
const selectedFilters = ref<{ id: string; name: string }[]>([])
const isBulkUpdateFormOpen = ref(false)
const isBulkUpdateBottomSheetOpen = ref(false)

const showEditForm = ref(false)

const idRef = computed(() => props.id)
// Paginated Fetch Jobs API
const bulkUpdateEnabled = useBulkUpdate()
const filters = useBatchViewFilters(idRef, selectedFilters)
const { getCurrentPage, updatePage } = usePagination()
const itemsPerPage = ref(20)
const query = reactive(
  useJobsQuery(
    filters,
    getCurrentPage,
    itemsPerPage,
    ref(['requested_start', 'number']), // sortBy
    ref([false, false]) // sortDesc
  )
)
const {
  jobs: batchJobs,
  isLoading,
  error: jobsError,
  reFetch: reFetchJobs,
} = useJobsSearch(query)

const {
  data: batch,
  isLoading: isBatchLoading,
  refetch: refetchBatch,
} = useInvoiceQuery(idRef)

const { data: batchSyncTargets } = useInvoiceSyncTargetsQuery(idRef)

const { data: syncOptions } = useGetSyncOptionsQuery()

const canViewBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canViewBatch']
)
const canEditBatch = computed<(batchOwnerId: string | null) => boolean>(
  () => store.getters['permissions/canEditBatch']
)

// Export CSV
const {
  exportBatchToCsv,
  saving: exportCsvSaving,
  error: exportCsvError,
} = useBatchCsvExport(idRef)

const {
  exportBatchToPdf,
  saving: exportPdfSaving,
  error: exportPdfError,
} = useBatchPdfExport(idRef)

// --------------------------------------Lifecycle hooks------------------------------------------
onMounted(async () => {
  selectedFilters.value = jobStatuses
})
onBeforeRouteUpdate(async (to, from, next) => {
  if (from.params.id === 'new' && !to.params.id) {
    await Promise.all([reFetchJobs(), refetchBatch()])
    selectedJobs.value = batchJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status)
    )
  }
  next()
})
onBeforeUnmount(() => {
  selectedJobs.value = []
  showSnackbarError.value = false
})

watch(Object.values(filters), () => {
  updatePage(1)
})
// ----------------------------------End Lifecycle hooks------------------------------------------

// -------------------------------------------Vuex------------------------------------------------
const jobIsSaving = computed<boolean>(() => store.state.jobs.saving)
watch(jobIsSaving, async (isSaving, wasSaving) => {
  if (wasSaving && !isSaving) {
    await Promise.all([reFetchJobs(), refetchBatch()])
    selectedJobs.value = batchJobs.value.filter(
      (job) =>
        selectedJobs.value.map((j) => j.number).includes(job.number) &&
        selectedFilters.value.map((f) => f.id).includes(job.status)
    )
  }
})

const selectedJobs = ref<JobSearchResponse[]>([])

// --------------------------------------Computed properties--------------------------------------
const filterOptions = computed(() =>
  jobStatuses.map((s) => ({
    ...s,
    count: jobStatusCount.value[s.id] || 0,
  }))
)

const formattedBatchDate = computed(() =>
  batch.value
    ? moment.utc(batch.value.invoiceDate).local().format('MMM DD YYYY')
    : ''
)

const formattedDueDate = computed(() =>
  batch.value && batch.value.dueDate
    ? moment.utc(batch.value.dueDate).local().format('MMM DD YYYY')
    : ''
)

const formattedBatchTotalAmount = computed(() =>
  formatter.format(batch.value?.totalAmount ?? 0)
)

const jobStatusCount = computed(() => ({
  [REQUESTED]: batch.value?.requestedJobCount ?? 0,
  [ASSIGNED_TO_SERVICE_PROVIDER]:
    batch.value?.assignedToServiceProviderJobCount ?? 0,
  [SCHEDULED]: batch.value?.scheduledJobCount ?? 0,
  [ACTIVE]: batch.value?.activeJobCount ?? 0,
  [PAUSED]: batch.value?.pausedJobCount ?? 0,
  [COMPLETED]: batch.value?.completedJobCount ?? 0,
  [CANCELLED]: batch.value?.cancelledJobCount ?? 0,
}))

const jobCountByStatus = computed(() => {
  const jobCountByStatus = {
    completed: batch.value?.completedJobCount,
    active:
      (batch.value?.activeJobCount ?? 0) + (batch.value?.pausedJobCount ?? 0),
    unstarted:
      (batch.value?.requestedJobCount ?? 0) +
      (batch.value?.scheduledJobCount ?? 0) +
      (batch.value?.assignedToServiceProviderJobCount ?? 0),
  }

  return orderedStatuses.map((status) => ({
    count: jobCountByStatus[status] || 0,
    name: status,
  }))
})

const syncTargets = computed(() => {
  const syncTargetAppIds = batchSyncTargets.value?.map((t) => t.id)

  const unusedSyncOptions =
    syncOptions.value
      ?.filter((option) => !syncTargetAppIds?.includes(option.id))
      .map((option) => ({
        id: option.id,
        name: option.name,
      })) ?? []

  return [...unusedSyncOptions, ...(batchSyncTargets.value ?? [])]
})

const isJobFormVisible = computed(
  () => route.name?.includes('ticket-info-panel') ?? false
)
const isSyncDetailsEditFormVisible = computed(() =>
  route.name?.includes('sync-details-edit')
)

const updateJobsLabel = computed(() => {
  if (selectedJobs.value.length === 1) return 'Update job'
  return `Update ${selectedJobs.value.length} jobs`
})

const showCheckbox = computed(() => selectedJobs.value.length > 0)

const areAllJobsSelected = computed(() => {
  const selectedJobIds = selectedJobs.value.map((j) => j.number)
  for (const job of batchJobs.value) {
    if (!selectedJobIds.includes(job.number)) {
      return false
    }
  }
  return true
})

const selectAllButtonLabel = computed(() =>
  areAllJobsSelected.value ? 'Deselect all' : 'Select all'
)
// ----------------------------------End Computed properties--------------------------------------

// ----------------------------------------Methods------------------------------------------------
function dismiss() {
  router.push({
    name: props.parentRouteName,
    params: props.parentRouteParams,
  })
}

function clearSnackbarMessage() {
  snackbarMessage.value = null
}

function clearSnackbarError() {
  showSnackbarError.value = false
}

function isJobSelected(job: JobSearchResponse) {
  return selectedJobs.value.map((job) => job.number).includes(job.number)
}

function handleSelectDeselectAll() {
  if (areAllJobsSelected.value) {
    selectedJobs.value = []
  } else {
    batchJobs.value.forEach((job) => {
      if (!selectedJobs.value.map((j) => j.number).includes(job.number)) {
        selectedJobs.value.push(job)
      }
    })
  }
}

async function handleBulkUpdate(updatedJobs: JobSearchResponse[]) {
  await Promise.all([reFetchJobs(), refetchBatch()])
  selectedJobs.value = updatedJobs.filter(
    (job) =>
      selectedFilters.value.map((f) => f.id).includes(job.status) &&
      job.invoice_id === idRef.value
  )
  if (selectedJobs.value.length === 0) {
    isBulkUpdateFormOpen.value = false
  }
}

async function handleJobSaved(newJob: { jobNumber: number | null }) {
  await Promise.all([reFetchJobs(), refetchBatch()])
  selectedJobs.value = batchJobs.value.filter(
    (job) =>
      selectedJobs.value.map((j) => j.number).includes(job.number) &&
      job.number !== newJob.jobNumber &&
      selectedFilters.value.map((f) => f.id).includes(job.status)
  )
}

function toggleShowEditForm() {
  showEditForm.value = !showEditForm.value
}

// -------------------------------------End Methods------------------------------------------------

return { __sfc: true,orderedStatuses, jobStatuses, formatter, props, store, route, router, snackbarMessage, showSnackbarError, selectedFilters, isBulkUpdateFormOpen, isBulkUpdateBottomSheetOpen, showEditForm, idRef, bulkUpdateEnabled, filters, getCurrentPage, updatePage, itemsPerPage, query, batchJobs, isLoading, jobsError, reFetchJobs, batch, isBatchLoading, refetchBatch, batchSyncTargets, syncOptions, canViewBatch, canEditBatch, exportBatchToCsv, exportCsvSaving, exportCsvError, exportBatchToPdf, exportPdfSaving, exportPdfError, jobIsSaving, selectedJobs, filterOptions, formattedBatchDate, formattedDueDate, formattedBatchTotalAmount, jobStatusCount, jobCountByStatus, syncTargets, isJobFormVisible, isSyncDetailsEditFormVisible, updateJobsLabel, showCheckbox, areAllJobsSelected, selectAllButtonLabel, dismiss, clearSnackbarMessage, clearSnackbarError, isJobSelected, handleSelectDeselectAll, handleBulkUpdate, handleJobSaved, toggleShowEditForm, FormLayout, CreatedInfo, VsWrapper, BulkUpdateForm, RightDrawer, JobCard, BatchSideSheetFilter, BatchSideSheetProgressBar, BatchPaymentTerms, batchPaymentTermsString, BatchEditForm, BatchSyncCard, VsButton, VsText, JobSearchPagination, VsSnackbar, VsHeading, VsBottomSheet }
}

})